const _ = require("lodash");
const ErrorUtil = require("../../../shared/ErrorUtil");
const AT = require("../../core/vault/ActionTypes");
const StateReset = require("../../core/vault/StateReset");
const {extendIn, setIn} = require("../../../shared/ImmHelpers");
const {
	atPage,
	atUser,
	atSchool,
	atSchoolAccounts,
	atSchoolRosters,
	atSchoolRostersValidation,
	atSchoolRostersGeneration,
	atSchoolRostersProcessing,
	atSchoolAccountSchool,
	atSchoolAccountClass,
	atSchoolAccountStaff,
	atSchoolAccountStudent,
	atSchoolAccountPurchase,
	atSchoolEntities,
	atAllLocks,
	atClassBookTestBlock,
} = require("../../core/vault/StateCursors");
const OpsSchool = require("./OpsSchool");
const Slots = require("../../core/vault/Slots");
const MultiSlots = require("../../core/vault/MultiSlots");
const OpsUser = require("../../core/vault/OpsUser");
const OpsEditSave = require("../../core/vault/OpsEditSave");
const OpsLockOverrides = require("../../core/vault/OpsLockOverrides");
const SchoolPage = require("./SchoolPage");
const {getLevelFromGrade} = require("../../../shared/SchoolUtil");
const URLMake = require("../../../shared/URLMake");
const UpdateReport = require("../../report/vault/UpdateReport");
const LoadState = require("../../core/vault/util/LoadState");

// Used as a reset hook.
const doSchoolStateReset = function(state, initState, now) {
	state = extendIn(state, [], {school: OpsSchool.init()});
	return state;
};

const actionHandlers = {
	[AT.SCHOOL_INITIAL](vault, state, action) {
		const now = vault.clock.now();
		state = doSchoolStateReset(state, state, now);
		StateReset.addResetHook(vault, doSchoolStateReset);
		return state;
	},

	[AT.LOAD_SCHOOL_ENTITIES_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.schoolEntities(action.includeTeachers);
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},

	[AT.LOAD_SCHOOL_ENTITIES_SLOT_SUCCESS](vault, state, action) {
		return Slots.loadSuccess(state, action.slot, value => {
			return extendIn(value, [], action.data);
		});
	},

	[AT.LOAD_SCHOOL_ENTITIES_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.LOAD_SCHOOL_ACCOUNTS_REQUEST](vault, state, action) {
		const user = atUser(state).get();
		const purchaseID = OpsUser.getStaffedPurchaseID(user);
		return atSchool(state).set(school =>
			OpsSchool.loadAccountsStart(school, purchaseID, vault.api)
		);
	},

	[AT.LOAD_SCHOOL_ACCOUNTS_SUCCESS](vault, state, action) {
		return atSchool(state).set(school =>
			OpsSchool.loadAccountsSuccess(school, action.data)
		);
	},

	[AT.LOAD_SCHOOL_ACCOUNTS_FAIL](vault, state, action) {
		return atSchool(state).set(school =>
			OpsSchool.loadAccountsFail(school, action.error)
		);
	},

	[AT.LOAD_SCHOOL_ACCOUNTS_STUDENTS_REQUEST](vault, state, action) {
		const user = atUser(state).get();
		const purchaseID = OpsUser.getStaffedPurchaseID(user);
		return atSchool(state).set(school =>
			OpsSchool.loadAccountsStudentsStart(school, purchaseID, vault.api)
		);
	},

	[AT.LOAD_SCHOOL_ACCOUNTS_STUDENTS_SUCCESS](vault, state, action) {
		return atSchool(state).set(school =>
			OpsSchool.loadAccountsStudentsSuccess(school, action.data)
		);
	},

	[AT.LOAD_SCHOOL_ACCOUNTS_STUDENTS_FAIL](vault, state, action) {
		return atSchool(state).set(school =>
			OpsSchool.loadAccountsStudentsFail(school, action.error)
		);
	},

	[AT.LOAD_SINGLE_CLASS_REQUEST](vault, state, action) {
		const slot = Slots.table.singleClass(action.classID);
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},

	[AT.LOAD_SINGLE_CLASS_SUCCESS](vault, state, action) {
		return Slots.loadSuccess(state, action.slot, value => {
			return setIn(value, ["students"], action.data.students);
		});
	},

	[AT.LOAD_SINGLE_CLASS_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.GENERATE_SCHOOL_USERNAME_REQUEST](vault, state, action) {
		const purchase = atSchoolAccountPurchase(state).get();
		vault.api.generateSchoolUsername(purchase.id);
		return state;
	},

	[AT.GENERATE_SCHOOL_USERNAME_SUCCESS](vault, state, action) {
		const {usernames} = action.data;
		vault.emitEvent(
			"generateSchoolUsernameSuccess",
			usernames && usernames[0].join("")
		);
	},

	[AT.GENERATE_SCHOOL_USERNAME_FAIL](vault, state, action) {
		const {error, moreInfo} = action;
		vault.emitEvent("generateSchoolUsernameFail", error, moreInfo);
	},

	[AT.UPDATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST](vault, state, action) {
		const {userID, classID, schoolID, vals, scope} = action;
		const purchaseID =
			action.purchaseID || atSchoolAccountPurchase(state).get().id;
		if (scope === "staff") {
			const platformUserID = atSchoolAccountStaff(state, userID).getIn(
				"platformUserID"
			);
			vault.api.updateSchoolAccountStaff(
				userID,
				platformUserID,
				purchaseID,
				vals
			);
		} else if (scope === "student") {
			const platformUserID = atSchoolAccountStudent(state, userID).getIn(
				"platformUserID"
			);
			vault.api.updateSchoolAccountStudent(
				userID,
				platformUserID,
				purchaseID,
				vals
			);
		} else if (scope === "class") {
			vault.api.updateSchoolAccountClass(classID, vals);
		} else if (scope === "school") {
			vault.api.updateSchoolAccountSchool(schoolID, vals);
		} else if (scope === "purchase") {
			vault.api.updateSchoolAccountPurchase(purchaseID, vals);
		}
		return state;
	},

	[AT.UPDATE_SCHOOL_ACCOUNTS_SCOPE_SUCCESS](vault, state, action) {
		const baseData = action.vals;
		const scope = action.scope;
		const entities = atSchoolEntities(state).get();
		const schoolAccount = atSchoolAccountPurchase(state).get();
		const isEntitiesLoaded = !_.isEmpty(entities);
		const isSchoolAccountLoaded = !_.isEmpty(schoolAccount);

		// Check if this is school accounts is loaded first
		if (scope === "staff") {
			// We only have to check load in the staff case because the
			// staff can get to their personal staff profile page without
			// loading anything from school accounts.
			if (baseData.email) {
				if (isSchoolAccountLoaded) {
					state = atSchoolAccountStaff(state, action.userID).setIn(
						"newEmail",
						baseData.email
					);
				}
				if (action.userID === state.user.userID) {
					state = atUser(state).setIn(["model", "newEmail"], baseData.email);
				}
			} else {
				if (isSchoolAccountLoaded) {
					state = atSchoolAccountStaff(state, action.userID).extend(baseData);
				}
				if (action.userID === state.user.userID) {
					state = atUser(state).extendIn(
						"model",
						_.pick(baseData, ["firstName", "lastName", "displayName"])
					);
				}
				if (isEntitiesLoaded) {
					const staffIndex = _.findIndex(
						entities,
						e => e.id === action.userID && e.type === "staff"
					);
					if (staffIndex > -1)
						state = atSchoolEntities(state).extendIn(
							[staffIndex, "val"],
							baseData
						);
				}
			}
		} else if (scope === "student") {
			state = atSchoolAccountStudent(state, action.userID).extend(baseData);
			const studentIndex = _.findIndex(
				entities,
				e => e.id === action.userID && e.type === "student"
			);
			if (studentIndex > -1)
				state = atSchoolEntities(state).extendIn(
					[studentIndex, "val"],
					baseData
				);
		} else if (scope === "class") {
			state = atSchoolAccountClass(state, action.classID).extend(baseData);
			const classIndex = _.findIndex(
				entities,
				e => e.id === action.classID && e.type === "class"
			);
			state = atSchoolEntities(state).setIn(
				[classIndex, "val"],
				baseData.displayName
			);
		} else if (scope === "school") {
			state = atSchoolAccountSchool(state, action.schoolID).extend(baseData);
			const schoolIndex = _.findIndex(
				entities,
				e => e.id === action.schoolID && e.type === "school"
			);
			state = atSchoolEntities(state).setIn(
				[schoolIndex, "val"],
				baseData.displayName
			);
		} else if (scope === "purchase") {
			state = atSchoolAccountPurchase(state).extend(baseData);
			if (baseData.displayName) {
				// Also update single school name
				const allSchools = OpsSchool.getAllSchools(atSchool(state).get());
				state = atSchool(state).setIn(
					["accounts", "schools", allSchools[0].id, "displayName"],
					baseData.displayName
				);
			}
		}
		if (baseData.hasOwnProperty("gradeID")) {
			vault.emitEvent("updateClassSettingsSuccess");
		} else {
			vault.emitEvent("schoolAccountActionSuccess");
		}
		return state;
	},

	[AT.UPDATE_SCHOOL_ACCOUNTS_SCOPE_FAIL](vault, state, action) {
		const {error, moreInfo, vals} = action;
		if (vals.hasOwnProperty("gradeID")) {
			vault.emitEvent("updateClassSettingsFail", error, moreInfo);
		} else {
			vault.emitEvent("schoolAccountActionFail", error, moreInfo);
		}
		return state;
	},

	// Beast Builder can only be force locked and not force unlocked.
	[AT.SET_CLASS_BEASTBUILDER_LOCK_OVERRIDE_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal");
			return state;
		}
		const {value, classID} = action;
		const lockStatus = value ? null : "locked";
		state = atSchoolAccountClass(state, classID).set(c => {
			return OpsEditSave.doRequest(c, vault.emitEvent, "schoolAccountClass", {
				eventName: "classBeastbuilderLock",
				doSave: () =>
					vault.api.setSpaceLockOverride(
						"beastbuilder",
						lockStatus,
						null,
						classID
					),
				dataToSet: [["beastLock", lockStatus]],
				optimistic: true,
			});
		});
		return state;
	},

	[AT.SET_CLASS_BEASTBUILDER_LOCK_OVERRIDE_SUCCESS](vault, state, action) {
		const {classID} = action;
		return atSchoolAccountClass(state, classID).set(c => {
			return OpsEditSave.onSuccess(c, vault.emitEvent, "schoolAccountClass");
		});
	},

	[AT.SET_CLASS_BEASTBUILDER_LOCK_OVERRIDE_FAIL](vault, state, action) {
		// Revert back to old value
		const {classID, error, moreInfo} = action;
		return atSchoolAccountClass(state, classID).set(c => {
			return OpsEditSave.onFail(
				c,
				vault.emitEvent,
				"schoolAccountClass",
				error,
				moreInfo
			);
		});
	},

	[AT.CREATE_SCHOOL_ACCOUNTS_SCOPE_REQUEST](vault, state, action) {
		const {classID, schoolID, purchaseID, vals, scope} = action;
		if (_.includes(["teacher", "schoolAdmin", "purchaseAdmin"], scope)) {
			let role = scope;
			if (role === "teacher" && !classID) {
				role = "auditor";
			}
			vault.api.createSchoolAccountStaff({
				purchaseID,
				vals,
				role,
				schoolID,
				classID,
			});
		} else if (scope === "student") {
			vault.api.createSchoolAccountStudent({classID, vals});
		} else if (scope === "class") {
			vault.api.createSchoolAccountClass({schoolID, vals});
		} else if (scope === "school") {
			vault.api.createSchoolAccountSchool({purchaseID, vals});
		}
		return state;
	},

	[AT.CREATE_SCHOOL_ACCOUNTS_SCOPE_SUCCESS](vault, state, action) {
		const {classID, schoolID, vals, scope, data, role} = action;
		const entities = atSchoolEntities(state).get();
		if (scope === "staff") {
			const staff = {};
			staff[data.userID] = initStaff(data.userID, vals);
			if (role === "purchaseAdmin") {
				staff[data.userID].current.adminPurchase = true;
				staff[data.userID].ever.adminPurchase = true;
			} else if (role === "schoolAdmin") {
				staff[data.userID].current.adminSchoolIDs = [schoolID];
				staff[data.userID].ever.adminSchoolIDs = [schoolID];
			} else if (role === "teacher") {
				staff[data.userID].current.teachClassIDs = [classID];
				staff[data.userID].ever.teachClassIDs = [classID];
			} else if (role === "auditor") {
				if (schoolID) {
					staff[data.userID].current.auditSchoolIDs = [schoolID];
					staff[data.userID].ever.auditSchoolIDs = [schoolID];
				} else {
					staff[data.userID].current.auditPurchase = true;
					staff[data.userID].ever.auditPurchase = true;
				}
			}
			state = atSchoolAccounts(state).extendIn("staff", staff);
			entities.push({
				id: data.userID,
				val: vals,
				type: "staff",
			});
		} else if (scope === "student") {
			const student = {};
			student[data.studentID] = {id: data.studentID, active: true, ...vals};
			state = atSchoolAccounts(state).extendIn("students", student);
			// Also add the student to the class they are in
			const classStudents = atSchoolAccounts(state).getIn([
				"classes",
				classID,
				"studentIDs",
			]);
			const withNewStudent = classStudents.concat([data.studentID]);
			state = atSchoolAccounts(state).setIn(
				["classes", classID, "studentIDs"],
				withNewStudent
			);
			entities.push({
				id: data.studentID,
				val: vals,
				type: "student",
			});
		} else if (scope === "class") {
			if (!vals.gradeID) {
				vals.gradeID = getLevelFromGrade(vals.displayedGrade);
			}
			state = atSchoolAccounts(state).extendIn("classes", {
				[data.classID]: {
					id: data.classID,
					active: true,
					...vals,
					schoolID,
					studentIDs: [],
					activeStudentIDs: [],
				},
			});
			// Also add the class to the list of classes in the school
			const schoolClasses = atSchoolAccounts(state).getIn([
				"schools",
				schoolID,
				"classIDs",
			]);
			const withNewClass = schoolClasses.concat([data.classID]);
			state = atSchoolAccounts(state).setIn(
				["schools", schoolID, "classIDs"],
				withNewClass
			);
			entities.push({
				id: data.classID,
				val: vals.displayName,
				type: "class",
			});
			// Redirect to class account page
			state = atPage(state).set(
				URLMake.state.forClassAccount(data.classID, "classes")
			);
		} else if (scope === "school") {
			state = atSchoolAccounts(state).extendIn("schools", {
				[data.schoolID]: {
					id: data.schoolID,
					active: true,
					...vals,
					classIDs: [],
				},
			});
			entities.push({
				id: data.schoolID,
				val: vals.displayName,
				type: "school",
			});
		}
		state = atSchoolEntities(state).set(entities);
		vault.emitEvent("schoolAccountActionSuccess");
		return state;
	},

	[AT.CREATE_SCHOOL_ACCOUNTS_SCOPE_FAIL](vault, state, action) {
		const {error, moreInfo} = action;
		vault.emitEvent("schoolAccountActionFail", error, moreInfo);
		if (action.error === "E_USERNAME_NOT_CHECKED_OUT") {
			vault.emitEvent("generateSchoolUsernameReset");
		}
		return state;
	},

	[AT.UPDATE_SCHOOL_STAFF_ROLE_REQUEST](vault, state, action) {
		const {classID, schoolID, purchaseID, role, enabled, userID} = action;
		vault.api.updateSchoolStaffRole({
			purchaseID,
			role,
			userID,
			schoolID,
			classID,
			enabled,
		});
		return state;
	},

	[AT.UPDATE_SCHOOL_STAFF_ROLE_SUCCESS](vault, state, action) {
		const {role, schoolID, classID, enabled, userID} = action;
		if (role === "purchaseAdmin") {
			state = atSchoolAccountStaff(state, userID).setIn(
				["current", "adminPurchase"],
				enabled
			);
		} else {
			const updateField = (field, scopeVal) => {
				const currUser = atSchoolAccountStaff(state, userID).getIn("current");
				const currList = currUser[field];
				let newList;
				if (enabled) {
					newList = _.uniq(currList.concat([scopeVal]));
				} else {
					newList = _.without(currList, scopeVal);
				}
				state = atSchoolAccountStaff(state, userID).setIn(
					["current", field],
					newList
				);
			};

			if (role === "teacher") {
				updateField("teachClassIDs", classID);
			} else if (role === "schoolAdmin") {
				updateField("adminSchoolIDs", schoolID);
			} else if (role === "auditor") {
				if (schoolID) {
					updateField("auditSchoolIDs", schoolID);
				} else {
					state = atSchoolAccountStaff(state, userID).setIn(
						["current", "auditPurchase"],
						enabled
					);
				}
			}

			// Make sure user is active if enabled
			if (enabled) {
				state = atSchoolAccountStaff(state, userID).setIn("active", true);
			}
		}

		vault.emitEvent("schoolAccountActionSuccess");
		return state;
	},

	[AT.UPDATE_SCHOOL_STAFF_ROLE_FAIL](vault, state, action) {
		const {error, moreInfo} = action;
		vault.emitEvent("schoolAccountActionFail", error, moreInfo);
		return state;
	},

	[AT.UPDATE_SCHOOL_STUDENT_CLASS_REQUEST](vault, state, action) {
		const {classID, userID, purchaseID} = action;
		const classInfo = atSchoolAccountClass(state, classID).get();
		vault.api.updateSchoolStudentClass({
			purchaseID,
			userID,
			classID,
			year: classInfo.year,
		});
		return state;
	},

	[AT.UPDATE_SCHOOL_STUDENT_CLASS_SUCCESS](vault, state, action) {
		const {userID, classID: newClassID, year} = action;
		// Remove student from their current class
		const currClass = OpsSchool.getClassForStudent(
			atSchool(state).get(),
			userID,
			year
		);
		if (currClass) {
			const currClassStudents = atSchoolAccountClass(state, currClass.id).getIn(
				"studentIDs"
			);
			if (!_.isEmpty(currClassStudents)) {
				state = atSchoolAccountClass(state, currClass.id).setIn(
					"studentIDs",
					_.without(currClassStudents, userID)
				);
			}
		}

		// Add student into their new class
		const newClassStudents = atSchoolAccountClass(state, newClassID).getIn(
			"studentIDs"
		);
		state = atSchoolAccountClass(state, newClassID).setIn(
			"studentIDs",
			_.concat(newClassStudents, userID)
		);

		// Make sure student is active (can only be transferred into an
		// active class)
		state = atSchoolAccountStudent(state, userID).setIn("active", true);

		vault.emitEvent("schoolAccountActionSuccess");
		return state;
	},

	[AT.UPDATE_SCHOOL_STUDENT_CLASS_FAIL](vault, state, action) {
		const {error, moreInfo} = action;
		vault.emitEvent("schoolAccountActionFail", error, moreInfo);
		return state;
	},

	[AT.SET_ACTIVE_SCHOOL_ACCOUNTS_USER_REQUEST](vault, state, action) {
		const {userID, purchaseID, isStaff, active} = action;
		vault.api.setActiveSchoolUser({
			purchaseID,
			userID,
			isStaff,
			active,
		});
		return state;
	},

	[AT.SET_ACTIVE_SCHOOL_ACCOUNTS_USER_SUCCESS](vault, state, action) {
		const {userID, isStaff, active} = action;
		if (isStaff) {
			state = atSchoolAccountStaff(state, userID).setIn("active", active);
		} else {
			state = atSchoolAccountStudent(state, userID).setIn("active", active);

			const studentClasses = atSchoolAccountStudent(state, userID).getIn(
				"classIDs"
			);
			_.each(studentClasses, c => {
				const activeStudentIDs = atSchoolAccountClass(state, c).getIn(
					"activeStudentIDs"
				);
				const updatedIDs = _.filter(activeStudentIDs, sid => sid !== userID);
				state = atSchoolAccountClass(state, c).setIn(
					"activeStudentIDs",
					updatedIDs
				);
			});
		}
		vault.emitEvent("schoolAccountActionSuccess");
		return state;
	},

	[AT.SET_ACTIVE_SCHOOL_ACCOUNTS_USER_FAIL](vault, state, action) {
		const {error, moreInfo} = action;
		vault.emitEvent("schoolAccountActionFail", error, moreInfo);
		return state;
	},

	[AT.SET_ACTIVE_SCHOOL_ACCOUNTS_SCOPE_REQUEST](vault, state, action) {
		const {schoolID, classID, active} = action;
		vault.api.setActiveSchoolAccountScope({
			schoolID,
			classID,
			active,
		});
		return state;
	},

	[AT.SET_ACTIVE_SCHOOL_ACCOUNTS_SCOPE_SUCCESS](vault, state, action) {
		const {classID, schoolID, active} = action;
		if (classID) {
			state = atSchoolAccountClass(state, classID).setIn("active", active);
		} else if (schoolID) {
			state = atSchoolAccountSchool(state, schoolID).setIn("active", active);
		}
		vault.emitEvent("schoolAccountActionSuccess");
		return state;
	},

	[AT.SET_ACTIVE_SCHOOL_ACCOUNTS_SCOPE_FAIL](vault, state, action) {
		const {error, moreInfo} = action;
		vault.emitEvent("schoolAccountActionFail", error, moreInfo);
		return state;
	},

	[AT.UPDATE_SCHOOL_CLASS_SETTINGS_REQUEST](vault, state, action) {
		const {classID, schoolID, settings, purchaseID} = action;
		vault.api.updateSchoolClassSettings({
			classID,
			schoolID,
			settings,
			purchaseID,
		});
		return state;
	},

	[AT.UPDATE_SCHOOL_CLASS_SETTINGS_SUCCESS](vault, state, action) {
		const {classID, schoolID, settings} = action;
		if (classID) {
			state = atSchoolAccountClass(state, classID).extendIn(
				"settings",
				settings
			);
		} else if (schoolID) {
			state = atSchoolAccountSchool(state, schoolID).extendIn(
				"classDefaults",
				settings
			);
		} else {
			state = atSchoolAccountPurchase(state).extendIn(
				"classDefaults",
				settings
			);
		}
		// Completely reset user locks after a locking scheme change.
		if (settings.lockSettings) {
			state = atAllLocks(state).set(OpsLockOverrides.resetAllUsers);
		}
		vault.emitEvent("updateClassSettingsSuccess");
		return state;
	},

	[AT.UPDATE_SCHOOL_CLASS_SETTINGS_FAIL](vault, state, action) {
		const {error, moreInfo} = action;
		vault.emitEvent("updateClassSettingsFail", error, moreInfo);
		return state;
	},

	[AT.REPORT_UNLOCK_BOOK_TEST_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "setLockOverride");
			return state;
		}
		const {classID, bookTest} = action;
		const blockCR = atClassBookTestBlock(state, classID, bookTest);
		const blockID = blockCR.getIn("blockID");
		if (!blockID) {
			ErrorUtil.log(
				"E_NO_BOOK_TEST_BLOCK_ID",
				`Tried to unlock class ${classID} book test ${bookTest} ` +
					`but no associated block ID`
			);
			return state;
		}
		vault.api.unlockBookTest(classID, blockID, bookTest);

		state = blockCR.setIn("unlocked", true);
		return state;
	},

	[AT.REPORT_UNLOCK_BOOK_TEST_SUCCESS](vault, state, action) {
		// Nothing?
		return state;
	},

	[AT.REPORT_UNLOCK_BOOK_TEST_FAIL](vault, state, action) {
		const {classID, bookTest} = action;
		const blockCR = atClassBookTestBlock(state, classID, bookTest);
		if (blockCR.exists()) {
			state = blockCR.setIn("unlocked", false);
			vault.emitEvent("showOfficeModal", "bookTestUnlockFail");
		}
		// If the above if failed, user might have logged out and dumped state. So
		// we'll just do nothing in that case.
		return state;
	},

	[AT.ROSTERING_VALIDATE_CSVS_REQUEST](vault, state, action) {
		return atSchoolRostersValidation(state)
			.canCreate()
			.set(r => {
				if (!state.connected) {
					return LoadState.loadError(r, "E_NOT_CONNECTED");
				}
				return LoadState.loadStart(r, () =>
					vault.api.validateRosters({
						purchaseID: action.purchaseID,
						staffRoster: action.staffRoster,
						studentRoster: action.studentRoster,
						year: action.year,
					})
				);
			});
	},

	[AT.ROSTERING_VALIDATE_CSVS_FAIL](vault, state, action) {
		return atSchoolRosters(state).set(r => {
			r = extendIn(r, "validation", LoadState.loadError(r, action.error));
			return r;
		});
	},

	[AT.ROSTERING_VALIDATE_CSVS_SUCCESS](vault, state, action) {
		const response = action.data;
		const rosterValidation = {
			...LoadState.loadSuccess({}),
			valid: response.valid,
			rosterErrors: response.rosterErrors,
			summary: response.summary,
		};

		return atSchoolRosters(state).set(r => {
			r = extendIn(r, "validation", rosterValidation);
			// Validation may have told us that this user accidentally
			// swapped their staff and student rosters. If so, we make that
			// swap in the vault too.
			if (response.shouldSwapRosters) {
				const staffRoster = _.get(r, "model.staffRoster");
				const studentRoster = _.get(r, "model.studentRoster");
				const newModel = {
					staffRoster: studentRoster,
					studentRoster: staffRoster,
				};
				r = extendIn(r, "model", newModel);
			}
			return r;
		});
	},

	[AT.ROSTERING_VALIDATE_CSVS_RESET](vault, state, action) {
		return atSchoolRostersValidation(state).set(LoadState.loadRestart({}));
	},

	[AT.ROSTERING_GENERATE_CSVS_REQUEST](vault, state, action) {
		return atSchoolRostersGeneration(state)
			.canCreate()
			.set(r => {
				if (!state.connected) {
					return LoadState.loadError(r, "E_NOT_CONNECTED");
				}
				return LoadState.loadStart(r, () => {
					vault.api.generateRoster({
						purchaseID: action.purchaseID,
						which: action.which,
						year: action.year,
					});
				});
			});
	},

	[AT.ROSTERING_GENERATE_CSVS_FAIL](vault, state, action) {
		return atSchoolRostersGeneration(state).set(r => {
			r = extendIn(r, "generation", LoadState.loadError(r, action.error));
			return r;
		});
	},

	[AT.ROSTERING_GENERATE_CSVS_SUCCESS](vault, state, action) {
		const rosterName = action.which + "Roster";
		const rosterContent = action.data.csv;
		return atSchoolRosters(state).set(r => {
			const blob = new Blob([rosterContent], {
				type: "text/csv",
			});
			r = extendIn(r, "generation", LoadState.loadSuccess({}));
			r = setIn(r, ["model", rosterName], {
				contents: rosterContent,
				url: URL.createObjectURL(blob),
				generated: true,
			});
			return r;
		});
	},

	[AT.ROSTERING_GENERATE_CSVS_RESET](vault, state, action) {
		state = atSchoolRosters(state).setIn("generation", LoadState.loadInit({}));
		state = atSchoolRosters(state).setIn("model", {});
		return state;
	},

	// Fired after a user selects a CSV roster to upload
	// in the rostering flow (either by dragging & dropping it
	// or selecting it from the file browser)
	[AT.ROSTERING_SELECT_CSV](vault, state, action) {
		const rosterModel = {
			fileInfo: action.file,
			contents: action.contents,
		};
		return atSchoolRosters(state).set(r => {
			r = setIn(r, "validation", LoadState.loadInit({}));
			r = setIn(r, "processing", LoadState.loadInit({}));
			return setIn(r, ["model", action.roster], rosterModel);
		});
	},

	[AT.ROSTERING_UPLOAD_SET_YEAR](vault, state, action) {
		return atSchoolRosters(state).setIn("year", action.year);
	},

	[AT.ROSTERING_RESET](vault, state, action) {
		return atSchoolRosters(state).set(r => {
			r = {
				validation: LoadState.loadInit({}),
				generation: LoadState.loadInit({}),
				processing: LoadState.loadInit({}),
			};
			return r;
		});
	},

	[AT.ROSTERING_GO_TO_STEP](vault, state, action) {
		return atPage(state).setIn("pageData", {
			rosteringStep: action.step.replace(" ", "-"),
		});
	},

	[AT.ROSTERING_PROCESS_CSVS_REQUEST](vault, state, action) {
		return atSchoolRostersProcessing(state)
			.canCreate(LoadState.loadInit({}))
			.set(p => {
				if (!state.connected) {
					return LoadState.loadError(p, "E_NOT_CONNECTED");
				}
				return LoadState.loadStart(p, () => {
					vault.api.processRosters({
						purchaseID: action.purchaseID,
						staffRoster: action.staffRoster,
						studentRoster: action.studentRoster,
						year: action.year,
					});
				});
			});
	},

	[AT.ROSTERING_PROCESS_CSVS_FAIL](vault, state, action) {
		return atSchoolRostersProcessing(state).set(p => {
			p = LoadState.loadError(p, action.error);
			return p;
		});
	},

	[AT.ROSTERING_PROCESS_CSVS_SUCCESS](vault, state, action) {
		const response = action.data;
		state = atSchoolRostersProcessing(state).set(p => {
			p = _.extend(p, response);
			return LoadState.loadSuccess(p);
		});
		state = atSchoolRostersGeneration(state).set(LoadState.loadInit({}));
		state = atSchoolRosters(state).setIn("model", {
			studentRoster: {},
			staffRoster: {},
		});
		// Trigger a reloading of the account data
		vault.emitEvent("processRostersSuccess");
		return state;
	},

	[AT.POST_ROSTER_PROCESSING_ACCOUNT_RELOAD_REQUEST](vault, state, action) {
		// This is similar to OpsSchool.loadAccountsStart,
		// but instead we skip from resetting the load state on that data.
		// This is because if we did that, it would cause the parent view
		// of the rostering flow to jarringly reload as if the page were refreshed.
		// Instead, we call the API without changing the existing load status,
		// and rely on the success action to update things.
		const user = atUser(state).get();
		const purchaseID = OpsUser.getStaffedPurchaseID(user);
		if (!purchaseID) {
			return atSchool(state).set(school =>
				OpsSchool.loadAccountsFail(school, "E_NO_PURCHASE")
			);
		}
		vault.api.loadSchoolAccounts(purchaseID, true);
	},

	[AT.POST_ROSTER_PROCESSING_ACCOUNT_RELOAD_SUCCESS](vault, state, action) {
		state = atSchool(state).set(school =>
			OpsSchool.loadAccountsSuccess(school, action.data)
		);
		vault.emitEvent("processRostersAccountReloadSuccess");
		return state;
	},

	[AT.POST_ROSTER_PROCESSING_ACCOUNT_RELOAD_FAIL](vault, state, action) {
		return atSchool(state).set(school =>
			OpsSchool.loadAccountsFail(school, action.error)
		);
	},

	[AT.POST_ROSTER_PROCESSING_ACCOUNT_STUDENTS_RELOAD_REQUEST](
		vault,
		state,
		action
	) {
		// This is similar to OpsSchool.loadAccountsStudentsStart,
		// but instead we skip from resetting the load state on that data.
		// This is because if we did that, it would cause the parent view
		// of the rostering flow to jarringly reload as if the page were refreshed.
		// Instead, we call the API without changing the existing load status,
		// and rely on the success action to update things.
		const user = atUser(state).get();
		const purchaseID = OpsUser.getStaffedPurchaseID(user);
		if (!purchaseID) {
			return atSchool(state).set(school =>
				OpsSchool.loadAccountsStudentsFail(school, "E_NO_PURCHASE")
			);
		}
		vault.api.loadSchoolAccountsStudents(purchaseID, true);
	},

	[AT.POST_ROSTER_PROCESSING_ACCOUNT_STUDENTS_RELOAD_SUCCESS](
		vault,
		state,
		action
	) {
		state = atSchool(state).set(school =>
			OpsSchool.loadAccountsStudentsSuccess(school, action.data)
		);
		// Reload school entities and report data
		const entitySlot = Slots.table.schoolEntities();
		const reportClassesMultiSlot = MultiSlots.table.reportClasses();
		const reportStudentsMultiSlot = MultiSlots.table.reportStudents();
		if (_.get(state, reportClassesMultiSlot.keyList)) {
			state = MultiSlots.clearAll(state, reportClassesMultiSlot);
		}
		if (_.get(state, reportStudentsMultiSlot.keyList)) {
			state = MultiSlots.clearAll(state, reportStudentsMultiSlot);
		}
		return Slots.loadRestartMany(state, [entitySlot]);
	},

	[AT.POST_ROSTER_PROCESSING_ACCOUNT_STUDENTS_RELOAD_FAIL](
		vault,
		state,
		action
	) {
		return atSchool(state).set(school =>
			OpsSchool.loadAccountsStudentsFail(school, action.error)
		);
	},

	[AT.ADD_SEATS_REQUEST](vault, state, action) {
		vault.api.addSeats(_.omit(action, ["type"]));
	},

	[AT.ADD_SEATS_SUCCESS](vault, state, action) {
		state = atSchoolAccountPurchase(state).setIn(
			"studentLimit",
			action.data.purchase.studentLimit
		);
		state = atSchoolAccountPurchase(state).setIn(
			"teacherLimit",
			action.data.purchase.teacherLimit
		);
		vault.emitEvent("addSeatsSuccess", action.data);
		return state;
	},

	[AT.ADD_SEATS_FAIL](vault, state, action) {
		vault.emitEvent("addSeatsFailure", action.data);
	},
};

const initStaff = function(userID, initVals) {
	return {
		id: userID,
		active: true,
		...initVals,
		current: {
			adminPurchase: false,
			teachClassIDs: [],
			adminSchoolIDs: [],
			auditPurchase: false,
			auditSchoolIDs: [],
		},
		ever: {
			adminPurchase: false,
			teachClassIDs: [],
			adminSchoolIDs: [],
			auditPurchase: false,
			auditSchoolIDs: [],
		},
	};
};

const validateSchoolPage = function(vault, oldState, newState, action) {
	return SchoolPage.validateSchoolPage(newState);
};

const checkPageChange = function(vault, oldState, newState, action) {
	const newPage = atPage(newState).get();
	if (newPage.name !== "school.main") {
		return newState;
	}
	if (newPage.space === "reports") {
		return SchoolPage.checkReportDefault(oldState, newState);
	}
};

const augment = function(updater) {
	updater.addActionHandler(actionHandlers);
	updater.addActionHandler(UpdateReport.actionHandlers);

	updater.addPostUpdateHook(
		validateSchoolPage,
		"school:validateSchoolPage",
		-1000
	);
	updater.addPostUpdateHook(checkPageChange, "school:checkPageChange");
};

module.exports = {
	augment,
};
